import React from "react";
import { Search } from "lucide-react";
import { SIDE_EFFECT_CATALOG, EFFECT_CATEGORIES } from "@/lib/sideEffectsCatalog";
import { SEVERITY_META, TREND_META } from "./constants";

export default function SideEffectLogPanel({
  effects,
  newEffect, setNewEffect,
  selectedEffectId, pickCatalogEffect,
  severity, setSeverity,
  trend, setTrend,
  frequency, setFrequency,
  onSubmit,
  onOpenAll,
}) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-soft mt-6" data-testid="side-effect-log-panel">
      <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
        <h3 className="font-display font-semibold text-stone-900">Side-effect log</h3>
        {effects.length > 0 && (
          <button onClick={onOpenAll} data-testid="view-all-effects-btn" className="text-xs font-semibold text-brand-700 hover:text-brand-700 inline-flex items-center gap-1">
            View all effects <Search className="w-3 h-3" />
          </button>
        )}
      </div>
      <form onSubmit={onSubmit} className="p-4 border-b border-stone-100 space-y-3" data-testid="side-effect-form">
        <div>
          <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Effect (optional)</label>
          <select
            data-testid="side-effect-catalog"
            value={selectedEffectId}
            onChange={(e) => pickCatalogEffect(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm"
          >
            <option value="">— Free-text only —</option>
            {EFFECT_CATEGORIES.filter((c) => c.id !== "all").map((cat) => (
              <optgroup key={cat.id} label={`${cat.icon} ${cat.label}`}>
                {SIDE_EFFECT_CATALOG.filter((eff) => eff.category === cat.id).map((eff) => (
                  <option key={eff.id} value={eff.id}>{eff.icon} {eff.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Note</label>
          <textarea
            data-testid="side-effect-input"
            value={newEffect}
            onChange={(e) => setNewEffect(e.target.value.slice(0, 500))}
            placeholder="How is the patient feeling? Any new or ongoing symptoms?"
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none text-sm resize-none"
          />
          <div className="text-[11px] text-stone-400 text-right mt-1" data-testid="side-effect-char-count">{newEffect.length} / 500</div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Severity</label>
            <select data-testid="side-effect-severity" value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-full px-2 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm">
              <option value="">— Select —</option>
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="expected">Expected</option>
              <option value="painful">Painful</option>
              <option value="severe">Severe</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Trend</label>
            <select data-testid="side-effect-trend" value={trend} onChange={(e) => setTrend(e.target.value)} className="w-full px-2 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm">
              <option value="">— Select —</option>
              <option value="improving">📈 Improving</option>
              <option value="stable">➡️ Stable</option>
              <option value="expected">Expected</option>
              <option value="normal">Normal</option>
              <option value="deteriorating">📉 Deteriorating</option>
              <option value="worsening">📉 Worsening</option>
              <option value="monitoring">🔍 Monitoring</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-stone-500 font-medium block mb-1.5">Frequency (optional)</label>
          <input
            data-testid="side-effect-frequency"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            placeholder="e.g. 3x daily, Constant, Intermittent"
            className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm"
          />
        </div>
        <div className="flex justify-end">
          <button data-testid="add-side-effect-btn" className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold">Add entry</button>
        </div>
      </form>
      <div className="px-5 pt-3 pb-1">
        <h4 className="text-[11px] uppercase tracking-wider text-stone-700 font-bold">Recent effects (top 5)</h4>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {effects.length === 0 && <div className="p-4 text-sm text-stone-500 text-center">No entries yet.</div>}
        {effects.slice(0, 5).map((e) => {
          const sev = e.severity ? SEVERITY_META[e.severity] : null;
          const trd = e.trend ? TREND_META[e.trend] : null;
          return (
            <div key={e.id} className="px-5 py-3 border-b border-stone-100 last:border-0" data-testid={`side-effect-entry-${e.id}`}>
              <div className="flex items-start gap-2.5">
                {e.icon && <div className="text-2xl shrink-0" aria-hidden="true">{e.icon}</div>}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-1">
                    {e.effect_name && <span className="font-semibold text-stone-900 text-sm">{e.effect_name}</span>}
                    {sev && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${sev.cls}`} title={`Severity: ${sev.label}`}>
                        <sev.Icon className="w-3 h-3" />{sev.label}
                      </span>
                    )}
                    {trd && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${trd.cls}`} title={`Trend: ${trd.label}`}>
                        <trd.Icon className="w-3 h-3" />{trd.label}
                      </span>
                    )}
                  </div>
                  {e.frequency && <div className="text-xs text-stone-600 mb-1">Frequency: <b className="text-stone-900">{e.frequency}</b></div>}
                  {e.note && <div className="text-sm text-stone-800 whitespace-pre-wrap">{e.note}</div>}
                  <div className="text-xs text-stone-500 mt-1">{new Date(e.created_at).toLocaleString()}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
